import { assign, createMachine } from "xstate";

import { fisherYatesShuffle } from "../../helpers/shuffle";

import { Word, Words } from "./types";

export const gameMachine = createMachine(
  {
    id: "game",
    initial: "Initial",
    states: {
      Initial: {
        on: {
          ON_GAME_START: {
            target: "IsIntroAudioPlaying",
            actions: ["handleStartGame"],
          },
        },
      },
      IsIntroAudioPlaying: {
        on: {
          ON_INTRO_AUDIO_END: {
            target: "IsGuessing",
          },
        },
      },
      HasGuessedCorrect: {
        initial: "Initial",
        id: "guess_correct",
        states: {
          Initial: {
            after: {
              "1000": {
                target: "GuessCorrectMedia",
              },
            },
          },
          GuessCorrectMedia: {
            type: "parallel",
            states: {
              IsReactionVideoPlaying: {
                on: {
                  ON_REACTION_VIDEO_END: {
                    target: "#guess_correct.IsFading",
                    actions: ["handleResetSelectedWord"],
                  },
                  ON_WIN_AUDIO_END: {
                    target: "IsWinAudioPlaying.End",
                  },
                },
              },
              IsWinAudioPlaying: {
                initial: "Wait",
                states: {
                  Wait: {
                    after: {
                      "4000": {
                        target: "Play",
                      },
                    },
                  },
                  Play: {},
                  End: {
                    type: "final",
                  },
                },
              },
            },
          },
          IsFading: {
            after: {
              "3000": {
                target: "#game.HasWon",
                actions: [],
              },
            },
          },
        },
      },
      HasGuessedWrong: {
        initial: "Initial",
        states: {
          Initial: {
            after: {
              "3000": {
                target: "IsReactionVideoPlaying",
                actions: [],
              },
            },
          },
          IsReactionVideoPlaying: {
            on: {
              ON_REACTION_VIDEO_END: {
                target: "IsFading",
                actions: ["handleResetSelectedWord"],
              },
            },
          },
          IsFading: {
            after: {
              "3000": {
                target: "IsShuffling",
                actions: ["handleShuffle"],
              },
            },
          },
          IsShuffling: {
            after: {
              "1000": {
                target: "#game.IsIntroAudioPlaying",
              },
            },
          },
        },
      },
      IsGuessing: {
        initial: "IsWordAudioPlaying",
        states: {
          IsWordAudioPlaying: {
            entry: ["resetWordIndex"],
            on: {
              ON_WORD_AUDIO_END: [
                {
                  cond: "canIncrementWordAudio",
                  actions: "handleIncrementWord",
                },
                {
                  target: "Waiting",
                },
              ],
            },
          },
          Waiting: {
            type: "final",
          },
        },
        on: {
          ON_GUESS: [
            {
              cond: "isGuessCorrect",
              target: "#game.HasGuessedCorrect",
              actions: ["handleSelectWord"],
            },
            {
              target: "#game.HasGuessedWrong",
              actions: ["handleSelectWord"],
            },
          ],
          ON_REPLAY_INTRO_AUDIO: {
            target: "IsIntroAudioPlaying",
          },
        },
      },
      HasWon: {
        on: {
          ON_GAME_START: {
            target: "IsIntroAudioPlaying",
            actions: ["handleStartGame"],
          },
        },
      },
    },
    schema: {
      context: {} as {
        readonly word: string;
        readonly words: Words;
        readonly selectedWord: Word | null;
        readonly wordAudioIndex: number;
        readonly successAudioUri?: string;
        readonly introAudioUri?: string;
      },
      events: {} as
        | {
            readonly type: "ON_GAME_START";
            readonly data: {
              readonly words: Words;
              readonly word: string;
              readonly successAudioUri?: string;
              readonly introAudioUri?: string;
            };
          }
        | { readonly type: "ON_GUESS"; readonly word: Word }
        | { readonly type: "ON_INTRO_AUDIO_END" }
        | { readonly type: "ON_WIN_AUDIO_END" }
        | { readonly type: "ON_SHUFFLE" }
        | { readonly type: "ON_REACTION_VIDEO_END" }
        | { readonly type: "ON_PLAY_WIN_AUDIO" }
        | { readonly type: "ON_PLAY_REACTION_VIDEO" }
        | { readonly type: "ON_WORD_AUDIO_END" }
        | { readonly type: "ON_AUDIO_END" }
        | { readonly type: "ON_REPLAY_INTRO_AUDIO" },
    },
    context: { word: "", words: [], selectedWord: null, wordAudioIndex: 0 },
    predictableActionArguments: true,
    preserveActionOrder: true,
  },
  {
    guards: {
      canIncrementWordAudio: (context): boolean => context.wordAudioIndex < 2,
      isGuessCorrect: (context, event): boolean =>
        event.type === "ON_GUESS" && !!event?.word?.isCorrect,
    },
    actions: {
      handleIncrementWord: assign({
        wordAudioIndex: (context) => context.wordAudioIndex + 1,
      }),
      resetWordIndex: assign({
        wordAudioIndex: (_) => 0,
      }),
      handleStartGame: assign({
        selectedWord: (_) => null,
        word: (context, event) =>
          event.type === "ON_GAME_START" ? event.data.word : "",
        words: (context, event) =>
          event.type === "ON_GAME_START"
            ? fisherYatesShuffle(event.data.words)
            : [],
        introAudioUri: (context, event) =>
          event.type === "ON_GAME_START" ? event.data.introAudioUri : undefined,
        successAudioUri: (context, event) =>
          event.type === "ON_GAME_START"
            ? event.data.successAudioUri
            : undefined,
        wordAudioIndex: (_) => 0,
      }),
      handleSelectWord: assign({
        selectedWord: (context, event) =>
          event.type === "ON_GUESS" ? event.word : null,
      }),
      handleResetSelectedWord: assign({
        selectedWord: (_) => null,
      }),
      handleShuffle: assign({
        words: (context) => fisherYatesShuffle(context.words),
      }),
    },
  }
);
